import { useState, useEffect } from "react";
import { HSLookup } from "../Components/HSLookup";
import { Database } from "../Components/Database";
import { CCICreation } from "../Components/CCICreation/CCICreation";
import { CCIHistory } from "../Components/CCIHistory";
import PricingSection from "../Components/PricingSection";
import api from '../Services/api';
import toast from "react-hot-toast";

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  businessName?: string;
  phoneNumber?: string;
  streetAddress?: string;
  city?: string;
  country?: string;
  postalCode?: string;
  role: string;
  activeSubscriptionId?: string | null;
}


interface SubscriptionData {
  tier: string;
}

export default function Dashboard() {
  const [selectedTab, setSelectedTab] = useState<"lookup" | "database" | "cci" | "cciHistory">("database");
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await api.get("/auth/user", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUser(data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      if (user?.activeSubscriptionId) {
        try {
          const token = localStorage.getItem("token");
          const { data } = await api.get("/subscription/status", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setSubscriptionData(data);
        } catch (error) {
          console.error("Error fetching subscription data:", error);
        }
      }
    };

    fetchSubscriptionData();
  }, [user]);

  const hasCCIHistoryAccess = () => {
    if (!subscriptionData) return false;
    const tier = subscriptionData.tier?.toLowerCase();
    return tier === 'professional' || tier === 'premium';
  };

  useEffect(() => {
    if (selectedTab === "cciHistory" && !hasCCIHistoryAccess()) {
      setSelectedTab("database");
    }
  }, [selectedTab, subscriptionData]);

  const handleSubscribe = async (tier: string, interval: string) => {
    try {
      const token = localStorage.getItem("token");
      const userEmail = localStorage.getItem("userEmail");

      toast.loading("Redirecting to checkout...");

      const { data } = await api.post(
        "/stripe/checkout",
        {
          planId: tier,
          interval: interval,
          userId: token,
          email: userEmail,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.dismiss();
      window.location.href = data.url;
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to start checkout. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 dark:border-indigo-400 mb-4"></div>
          <p className="text-gray-700 dark:text-gray-300 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const hasActiveSubscription = user?.activeSubscriptionId && user.activeSubscriptionId.trim() !== "";

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className={`mx-auto py-6 sm:py-12 px-4 sm:px-6 lg:px-8 text-gray-900 dark:text-gray-100 ${hasActiveSubscription && selectedTab === "cciHistory"
        ? "max-w-full"
        : "max-w-7xl"
        }`}>
        {hasActiveSubscription ? (
          <>
            {/* Scrollable tab navigation for mobile */}
            <div className="overflow-x-auto pb-2 mb-4 sm:mb-8 hide-scrollbar">
              <div className="flex space-x-2 min-w-max">
                <button
                  onClick={() => setSelectedTab("database")}
                  className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium text-sm transition-colors duration-150 ${selectedTab === "database"
                    ? "bg-white dark:bg-gray-800 text-indigo-900 dark:text-indigo-600 shadow"
                    : "text-gray-600 dark:text-gray-300 hover:bg-white hover:text-indigo-900 dark:hover:bg-gray-700 dark:hover:text-indigo-400"
                    }`}
                >
                  Database
                </button>
                <button
                  onClick={() => setSelectedTab("cci")}
                  className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium text-sm transition-colors duration-150 ${selectedTab === "cci"
                    ? "bg-white dark:bg-gray-800 text-indigo-900 dark:text-indigo-600 shadow"
                    : "text-gray-600 dark:text-gray-300 hover:bg-white hover:text-indigo-900 dark:hover:bg-gray-700 dark:hover:text-indigo-400"
                    }`}
                >
                  CCI Creation
                </button>
                {hasCCIHistoryAccess() && (
                  <button
                    onClick={() => setSelectedTab("cciHistory")}
                    className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium text-sm transition-colors duration-150 ${selectedTab === "cciHistory"
                      ? "bg-white dark:bg-gray-800 text-indigo-900 dark:text-indigo-600 shadow"
                      : "text-gray-600 dark:text-gray-300 hover:bg-white hover:text-indigo-900 dark:hover:bg-gray-700 dark:hover:text-indigo-400"
                      }`}
                  >
                    CCI History
                  </button>
                )}
                {/* <button
                  onClick={() => setSelectedTab("lookup")}
                  className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium text-sm transition-colors duration-150 ${selectedTab === "lookup"
                    ? "bg-white dark:bg-gray-800 text-indigo-900 dark:text-indigo-600 shadow"
                    : "text-gray-600 dark:text-gray-300 hover:bg-white hover:text-indigo-900 dark:hover:bg-gray-700 dark:hover:text-indigo-400"
                    }`}
                >
                  HS Lookup
                </button> */}
              </div>
            </div>

            {/* Responsive content container */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 sm:p-6 overflow-hidden">
              {selectedTab === "lookup" ? <HSLookup /> :
                selectedTab === "database" ? <Database /> :
                  selectedTab === "cci" ? <CCICreation /> :
                    <CCIHistory />}
            </div>
          </>
        ) : (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
              Choose a Plan to Get Started
            </h2>
            <PricingSection onSubscribe={handleSubscribe} />
          </div>
        )}
      </div>
    </div>
  );

}
